import React, { useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import {
    Box, VStack, HStack, Text, Select, Checkbox, Table, Thead, Tbody, Tr, Th, Td,
    Tag, Button, Divider, Icon, Container, useColorModeValue, Spinner,
    Flex
} from '@chakra-ui/react';
import { LuCalendar, LuCheck } from 'react-icons/lu';
import { fetchWrapper } from '../../../helpers';
import { toast } from 'react-toastify';

const InvoiceGenerationForm = ({ onClose }) => {
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedClients, setSelectedClients] = useState(new Set());
    const [clients, setClients] = useState([]);
    const [clientsData, setClientsData] = useState([]);
    const [loading, setLoading] = useState(false);

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const currentMoment = moment();
    const currentYear = currentMoment.year();
    const currentMonth = currentMoment.month();

    const years = useMemo(() => {
        const yearsList = [];
        for (let year = currentYear; year >= currentYear - 5; year--) {
            yearsList.push(year);
        }
        return yearsList;
    }, [currentYear]);

    const availableMonths = useMemo(() => {
        if (!selectedYear) return months;
        const year = parseInt(selectedYear);
        return year === currentYear ? months.slice(0, currentMonth + 1) : months;
    }, [selectedYear]);

    const handleClientToggle = (clientId) => {
        const newSelected = new Set(selectedClients);
        newSelected.has(clientId) ? newSelected.delete(clientId) : newSelected.add(clientId);
        setSelectedClients(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedClients.size === clients.length) {
            setSelectedClients(new Set());
        } else {
            setSelectedClients(new Set(clients.map(client => client.customer_id)));
            setClientsData(clients.map(client => client));
        }
    };


    const totalEstimate = useMemo(() => {
        return clients
            .filter(client => selectedClients.has(client.customer_id))
            .reduce((sum, client) => sum + Number(client.estimate || 0), 0);
    }, [clients, selectedClients]);

    const formatBillingPeriod = () => {
        if (!selectedMonth || !selectedYear) return '';
        const monthIndex = months.indexOf(selectedMonth);
        const year = parseInt(selectedYear);
        const startDate = moment().year(year).month(monthIndex).startOf('month');
        const endDate = moment().year(year).month(monthIndex).endOf('month');
        return `${startDate.format('DD MMM YYYY')} - ${endDate.format('DD MMM YYYY')}`;
    };

    // 📦 Fetch invoice clients when both month and year are selected
    useEffect(() => {
        const fetchInvoices = async () => {
            if (!selectedMonth || !selectedYear) return;

            const monthIndex = months.indexOf(selectedMonth) + 1;
            const year = parseInt(selectedYear);

            setLoading(true);
            try {
                const url = `${process.env.REACT_APP_API_URL}/invoice/get-customer-invoice`
                const response = await fetchWrapper.post(url, {
                    month: monthIndex,
                    year: year
                });

                

                setClients(response || []);
                setSelectedClients(new Set()); // reset selection on data change
            } catch (error) {
                console.error('Failed to fetch invoices', error);
            } finally {
                setLoading(false);
            }
        };

        fetchInvoices();
    }, [selectedMonth, selectedYear]);


    const handleGenerateInvoices = async () => {
        if (selectedClients.size === 0 || !selectedMonth || !selectedYear) return;

        const customerIds = Array.from(selectedClients);
        const monthIndex = months.indexOf(selectedMonth) + 1;
        const year = parseInt(selectedYear);

        try {
            const url = `${process.env.REACT_APP_API_URL}/invoice/create`;
            const payload = {
                customer_id: customerIds,
                month: monthIndex,
                year: year,
            };

            const response = await fetchWrapper.post(url, payload);
          
            toast.success(response?.message)

            // ✅ Close modal on success
            if (onClose) {
                onClose();
            }

            // Optionally show a toast or success alert here
        } catch (error) {
            console.error('Failed to create invoices:', error);
            toast.error(error)
            // Optionally show error toast here
        }
    };



    return (
        <Container maxW="6xl" py={6}>
            <VStack spacing={8} align="stretch">
                {/* Header and Filters */}
                <Box>
                    <Text fontSize="2xl" fontWeight="bold" color="gray.800" mb={2}>
                        Generate Monthly Invoices
                    </Text>
                    <Text color="gray.600" fontSize="sm">
                        Select a billing month to view eligible clients and generate invoices.
                    </Text>
                </Box>

                {/* Select Month/Year */}
                <Box>
                    <HStack spacing={3} mb={4}>
                        <Icon as={LuCalendar} color="blue.500" boxSize={5} />
                        <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                            Step 1: Select Billing Month
                        </Text>
                    </HStack>

                    <HStack spacing={6} align="end">
                        <Box>
                            <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>Billing Month</Text>
                            <Select placeholder="Select month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} width="200px" bg="white">
                                {availableMonths.map(month => <option key={month}>{month}</option>)}
                            </Select>
                        </Box>

                        <Box>
                            <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>Year</Text>
                            <Select placeholder="Select year" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} width="150px" bg="white">
                                {years.map(year => <option key={year} value={year}>{year}</option>)}
                            </Select>
                        </Box>

                        <Box>
                            <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>Billing Period</Text>
                            <Text fontSize="sm" color="gray.600" py={2} px={3} bg="gray.50" borderRadius="md" minW="250px">
                                {formatBillingPeriod() || 'Please select month and year'}
                            </Text>
                        </Box>
                    </HStack>
                </Box>

                <Divider />

                {/* Step 2: Show Clients */}
                {selectedMonth && selectedYear && (
                    <Box>
                        <HStack spacing={3} mb={4}>
                            <Icon as={LuCheck} color="blue.500" boxSize={5} />
                            <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                                Step 2: Select Eligible Clients
                            </Text>
                        </HStack>

                        {loading ? (
                            <Box textAlign="center" py={10}>
                                <Spinner size="lg" color="blue.500" />
                            </Box>
                        ) : (
                            <>
                                <Box overflowX="auto">
                                    <Table variant="simple" bg="white" borderRadius="md" overflow="hidden">
                                        <Thead bg="gray.50">
                                            <Tr>
                                                <Th py={4}>
                                                    <Checkbox
                                                        isChecked={selectedClients.size === clients.length}
                                                        isIndeterminate={selectedClients.size > 0 && selectedClients.size < clients.length}
                                                        onChange={handleSelectAll}
                                                    />
                                                </Th>
                                                <Th>Client Name</Th>
                                                {/* <Th>Account Type</Th> */}
                                                <Th>Last Invoice</Th>
                                                <Th>Estimated Amount</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {clients.map((client) => (
                                                <Tr key={client.customer_id}>
                                                    <Td>
                                                        <Checkbox
                                                            isChecked={selectedClients.has(client.customer_id)}
                                                            onChange={() => handleClientToggle(client.customer_id)}
                                                        />
                                                    </Td>
                                                    <Td fontWeight="medium">{client.customer_name}</Td>
                                                    {/* <Td>
                            <Tag colorScheme="blue" fontSize="xs">{client?.accountType}</Tag>
                          </Td> */}
                                                    <Td color="gray.600">{client.last_invoice}</Td>
                                                    <Td fontWeight="semibold" color="green.600">{Number(client.estimate)?.toFixed(2)}</Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </Box>
                       
                                {/* Summary */}
                                <Box mt={8} p={6} bg="gray.50" borderRadius="md">
                                    <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>Invoice Generation Summary</Text>
                                    <Flex wrap="wrap" gap={8} align="flex-start">
                                        <Box minW="200px">
                                            <Text fontSize="sm" color="gray.600" mb={1}>Billing Month:</Text>
                                            <Text fontWeight="semibold" color="gray.800">{selectedMonth} {selectedYear}</Text>
                                        </Box>
                                        <Box minW="200px">
                                            <Text fontSize="sm" color="gray.600" mb={1}>Selected Clients:</Text>
                                            <Text fontWeight="semibold" color="gray.800">{selectedClients.size} clients</Text>
                                        </Box>
                                        <Box minW="220px">
                                            <Text fontSize="sm" color="gray.600" mb={1}>Total Estimated Amount:</Text>
                                            <Text fontWeight="semibold" color="green.600">
                                                Rs {totalEstimate.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </Text>
                                        </Box>
                                        <Box flex="1" minW="200px" textAlign={{ base: 'left', md: 'right' }}>
                                            <Button
                                                colorScheme="blue"
                                                size="lg"
                                                isDisabled={selectedClients.size === 0}
                                                onClick={handleGenerateInvoices}
                                            >
                                                Generate Invoices
                                            </Button>

                                        </Box>
                                    </Flex>


                                </Box>
                            </>
                        )}
                    </Box>
                )}
            </VStack>
        </Container>
    );
};

export default InvoiceGenerationForm;
